import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import "./LudNotifications.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { Button, NoticeBox, RefreshCcw, X } from "../ui";
import { usePageSection } from "../../navigation/pageSections";
import NotificationsInbox from "./NotificationsInbox/NotificationsInbox";
import SendNotification from "./SendNotification/SendNotification";
import TemplateBuilder from "./TemplateBuilder/TemplateBuilder";
import {
  fetchNotificationRestaurantsAPI,
  fetchNotificationTemplatesAPI,
  fetchNotificationsAPI,
  sendNotificationAPI,
  markNotificationReadAPI,
} from "../../utils/api/notifications";
import {
  getNotificationLink,
  getRecipientLabel,
  getText,
  isArabic,
} from "./utils/notificationHelpers";

const NOTIFICATION_COUNT_EVENT = "lud-notification-count-change";

const TABS = {
  INBOX: "inbox",
  SEND: "send",
  BUILDER: "builder",
};

const TEMPLATE_CATEGORIES = ["menu", "manager", "report", "ticket"];

const DEFAULT_FILTERS = {
  category: "all",
  ownerId: "",
  branchId: "",
  status: "all",
};

const initialTemplates = () =>
  TEMPLATE_CATEGORIES.reduce((acc, category) => {
    acc[category] = [];
    return acc;
  }, {});

const initialSendState = {
  composeCategory: "general",
  selectedTemplateId: "",
  recipientMode: "single",
  ownerId: "",
  ownerIds: [],
  scope: "owner",
  branchId: "",
  ownerSeesToo: true,
  note: "",
};

const getToken = () => localStorage.getItem("auth-token");

const getTemplateId = (template) => template?._id || template?.id || "";

const getNotificationId = (notification) =>
  notification?._id || notification?.id || "";

// Same order as the API (createdAt desc, then _id desc): used when notices are added locally.
const newestFirst = (a, b) =>
  new Date(b.createdAt || 0) - new Date(a.createdAt || 0) ||
  String(getNotificationId(b)).localeCompare(String(getNotificationId(a)));

const isDefaultFilters = (filters) =>
  Object.keys(DEFAULT_FILTERS).every(
    (key) => filters[key] === DEFAULT_FILTERS[key],
  );

const groupTemplates = (templates = []) => {
  const grouped = initialTemplates();

  templates.forEach((template) => {
    const category = template.category || template.cat;

    if (!grouped[category]) return;

    grouped[category].push({
      ...template,
      id: getTemplateId(template),
      cat: category,
    });
  });

  return grouped;
};

const formatNotificationTime = (date, lang, fallback) => {
  if (!date) return fallback;

  try {
    return new Date(date).toLocaleString(lang === "ar" ? "ar-SA" : "en-US", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return fallback;
  }
};

// Both language variants are precomputed (the card picks one by language),
// so switching language no longer requires refetching the list.
const normalizeNotification = (notification) => ({
  ...notification,
  id: getNotificationId(notification),
  time:
    notification.time ||
    formatNotificationTime(notification.createdAt, "ar", getText("ar").send.now),
  timeEn:
    notification.timeEn ||
    formatNotificationTime(notification.createdAt, "en", "Now"),
});

export default function LudNotifications() {
  const {
    lang = "ar",
    handleTokenExpiration,
    storeAccess,
    branchAccess,
  } = useContext(Context);
  const { can, has, needsBranchAssignment } = useAccess();
  const navigate = useNavigate();
  const t = getText(lang);
  const ar = isArabic(lang);

  // Inbox / Send / Builder are opened from the sidebar submenu (`?tab=`);
  // sections the user may not use fall back to the inbox.
  const [activeTab, setActiveTab] = usePageSection("notifications");
  const [restaurants, setRestaurants] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [listState, setListState] = useState({ status: "loading", error: "" });
  const [reloadKey, setReloadKey] = useState(0);
  const [templates, setTemplates] = useState(initialTemplates);
  const [sent, setSent] = useState([]);
  const [toast, setToast] = useState("");
  const [notice, setNotice] = useState(null);
  const [sending, setSending] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  // Unread total across all notifications (drives the tab and sidebar badges).
  // It must not follow the filtered list, or filtering by "read" would zero it.
  const [totalUnread, setTotalUnread] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sendState, setSendState] = useState(initialSendState);

  const toastTimer = useRef(null);
  // A ref (not just state) so a second click before the re-render is ignored.
  const sendingRef = useRef(false);
  const pendingRef = useRef(new Set());
  const listSeq = useRef(0);
  const tokenExpirationRef = useRef(handleTokenExpiration);
  useEffect(() => {
    tokenExpirationRef.current = handleTokenExpiration;
  });

  // Context rebuilds these arrays on each render; key on their contents.
  const storeAccessKey = JSON.stringify(storeAccess || []);
  const branchAccessKey = JSON.stringify(branchAccess || []);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = useCallback((message) => {
    clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = setTimeout(() => setToast(""), 3000);
  }, []);

  const reportError = useCallback(
    (error, fallback) => {
      console.error(error);
      setNotice({
        text: fallback || (ar ? "تعذّر تنفيذ العملية" : "The action failed"),
        detail: error?.message || "",
      });
    },
    [ar],
  );

  const loadTemplates = useCallback(async () => {
    const token = getToken();

    if (!token) {
      tokenExpirationRef.current?.();
      return;
    }

    const data = await fetchNotificationTemplatesAPI(token);
    setTemplates(groupTemplates(Array.isArray(data) ? data : []));
  }, []);

  // Restaurants and templates: loaded once (the notifications list is loaded
  // by the filters effect below; loading it here too doubled the request).
  useEffect(() => {
    const token = getToken();

    if (!token) {
      tokenExpirationRef.current?.();
      return;
    }

    let cancelled = false;
    Promise.all([
      fetchNotificationRestaurantsAPI(token, storeAccess || []),
      fetchNotificationTemplatesAPI(token),
    ])
      .then(([restaurantsData, templatesData]) => {
        if (cancelled) return;
        setRestaurants(Array.isArray(restaurantsData) ? restaurantsData : []);
        setTemplates(
          groupTemplates(Array.isArray(templatesData) ? templatesData : []),
        );
      })
      .catch((error) => {
        if (!cancelled)
          reportError(
            error,
            ar
              ? "تعذّر تحميل المطاعم أو القوالب"
              : "Could not load restaurants or templates",
          );
      });

    return () => {
      cancelled = true;
    };
    // reportError/ar only affect message text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeAccessKey]);

  // Notifications list: one request per filter change; responses that arrive
  // after a newer request started are ignored.
  useEffect(() => {
    const token = getToken();

    if (!token) {
      tokenExpirationRef.current?.();
      return;
    }

    const seq = ++listSeq.current;
    setListState((current) => ({ ...current, status: "loading" }));

    fetchNotificationsAPI(token, storeAccess || [], branchAccess || [], filters)
      .then((data) => {
        if (seq !== listSeq.current) return;
        const normalized = (Array.isArray(data) ? data : []).map(
          normalizeNotification,
        );
        setNotifications(normalized);
        if (isDefaultFilters(filters)) {
          setTotalUnread(normalized.filter((item) => item.unread).length);
        }
        setListState({ status: "ready", error: "" });
      })
      .catch((error) => {
        if (seq !== listSeq.current) return;
        console.error(error);
        setListState({ status: "error", error: error?.message || "" });
      });
    // storeAccess/branchAccess are tracked through their content keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, reloadKey, storeAccessKey, branchAccessKey]);

  const selectedTemplate = useMemo(() => {
    return Object.values(templates)
      .flat()
      .find(
        (template) => getTemplateId(template) === sendState.selectedTemplateId,
      );
  }, [templates, sendState.selectedTemplateId]);

  const stats = useMemo(() => {
    let unread = 0;
    notifications.forEach((item) => {
      if (item.unread) unread += 1;
    });
    return {
      all: notifications.length,
      unread,
      read: notifications.length - unread,
    };
  }, [notifications]);

  useEffect(() => {
    if (totalUnread === null) return;
    window.dispatchEvent(
      new CustomEvent(NOTIFICATION_COUNT_EVENT, {
        detail: { unreadCount: totalUnread },
      }),
    );
  }, [totalUnread]);

  // Guards per-notification actions against repeated clicks.
  const runForNotification = async (id, action) => {
    if (!id || pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    setPendingIds(new Set(pendingRef.current));
    try {
      await action();
    } finally {
      pendingRef.current.delete(id);
      setPendingIds(new Set(pendingRef.current));
    }
  };

  const replaceNotification = (id, updated) =>
    setNotifications((prev) =>
      prev.map((item) => (getNotificationId(item) === id ? updated : item)),
    );

  // A notification may be changed only by a user who reaches the restaurant it
  // belongs to (`scope.ownerId`, a public id) and, for a branch-level
  // notification, the branch it targets (NA-010 / NA-011). `scope.branchId` is a
  // branch `_id`; the user's assigned branches hold the `_id` or the name, so the
  // restaurants list (already loaded) is used to translate. Missing or
  // unresolvable identity is denied.
  const canChangeNotification = (permission, notification) => {
    const ownerId = notification?.scope?.ownerId;
    if (!can(permission, { restaurantId: ownerId })) return false;
    if (notification?.scope?.level !== "branch") return true;
    if (needsBranchAssignment) return false;
    const assigned = Array.isArray(branchAccess) ? branchAccess.filter(Boolean) : [];
    // No assigned branches: the restaurant check above is the whole rule.
    if (assigned.length === 0) return true;
    const owner = restaurants.find((item) => item.id === ownerId);
    const branch = owner?.branches?.find((item) => item.id === notification.scope.branchId);
    if (!branch) return false;
    return assigned.includes(branch.id) || assigned.includes(branch.name);
  };

  const denyChange = () =>
    setNotice({
      text: ar ? "غير مصرّح لك بتنفيذ هذا الإجراء" : "You are not allowed to do this",
      detail: "",
    });

  const markRead = (id) => {
    const current = notifications.find((item) => getNotificationId(item) === id);
    // Already read: nothing to send.
    if (!current?.unread) return;
    if (!canChangeNotification("notification.read.mark", current)) return denyChange();

    const token = getToken();
    if (!token) {
      tokenExpirationRef.current?.();
      return;
    }

    return runForNotification(id, async () => {
      try {
        const res = await markNotificationReadAPI(token, id);
        if (!res?.success || !res.notification) {
          throw new Error(res?.message || "");
        }
        const updated = normalizeNotification(res.notification);
        replaceNotification(id, updated);
        if (!updated.unread) {
          setTotalUnread((count) =>
            count === null ? count : Math.max(0, count - 1),
          );
        }
      } catch (error) {
        reportError(
          error,
          ar ? "تعذّر تعليم الإشعار كمقروء" : "Could not mark the notification as read",
        );
      }
    });
  };

  // Automated notifications link to the page of the record they describe. Opening one also marks it
  // read (when the user may); the destination's own route guard decides what the user can see.
  const openNotification = async (notification) => {
    const link = getNotificationLink(notification);
    if (!link) return;
    if (notification.unread && canChangeNotification("notification.read.mark", notification)) {
      await markRead(getNotificationId(notification));
    }
    navigate(link);
  };

  const resetSendState = () => setSendState(initialSendState);

  const sendNotification = async () => {
    // handler-level authorization: the role, then every recipient restaurant
    if (!has("notification.send")) return;
    if (sendingRef.current) return;

    const token = getToken();

    if (!token) {
      tokenExpirationRef.current?.();
      return;
    }

    if (!selectedTemplate) return;

    const hasRecipient =
      sendState.recipientMode === "multiple"
        ? sendState.ownerIds.length > 0
        : sendState.ownerId &&
          (sendState.scope !== "branch" || sendState.branchId);

    if (!hasRecipient) return;

    // Each recipient must be a restaurant from the loaded list (a verified public
    // id) that the user may notify; one that is not stops the whole send.
    const recipientIds =
      sendState.recipientMode === "multiple" ? sendState.ownerIds : [sendState.ownerId];
    const recipientsAllowed = recipientIds.every((ownerId) => {
      const owner = restaurants.find((item) => item.id === ownerId);
      if (!owner || !can("notification.send", { restaurantId: owner.id })) return false;
      return (
        sendState.scope !== "branch" ||
        sendState.recipientMode === "multiple" ||
        owner.branches?.some((branch) => branch.id === sendState.branchId)
      );
    });
    if (!recipientsAllowed) return denyChange();

    const recipientLabel = getRecipientLabel({
      restaurants,
      ...sendState,
      lang,
    });

    const scopes =
      sendState.recipientMode === "multiple"
        ? sendState.ownerIds.map((ownerId) => ({
            level: sendState.scope,
            ownerId,
            branchId: "",
            visibleToOwner: true,
          }))
        : [
            {
              level: sendState.scope,
              ownerId: sendState.ownerId,
              branchId: sendState.branchId || "",
              visibleToOwner:
                sendState.scope === "branch" ? sendState.ownerSeesToo : true,
            },
          ];

    sendingRef.current = true;
    setSending(true);
    try {
      // One request per recipient, sent together; each result is checked.
      const results = await Promise.allSettled(
        scopes.map((scope) =>
          sendNotificationAPI(token, {
            templateId: getTemplateId(selectedTemplate),
            note: sendState.note,
            scope,
          }),
        ),
      );

      const created = [];
      let firstError = null;
      results.forEach((result) => {
        if (
          result.status === "fulfilled" &&
          result.value?.success &&
          result.value.notification
        ) {
          created.push(normalizeNotification(result.value.notification));
        } else if (!firstError) {
          firstError =
            result.reason ||
            new Error(result.value?.message || "");
        }
      });

      if (firstError) {
        reportError(
          firstError,
          ar
            ? `تعذّر إرسال ${scopes.length - created.length} من ${scopes.length}`
            : `Could not send ${scopes.length - created.length} of ${scopes.length}`,
        );
      }

      if (!created.length) return;

      // Skip anything already in the list so a notification never appears twice.
      setNotifications((prev) => {
        const existing = new Set(prev.map(getNotificationId));
        return [
          ...created.filter((item) => !existing.has(getNotificationId(item))),
          ...prev,
        ].sort(newestFirst);
      });
      const newUnread = created.filter((item) => item.unread).length;
      if (newUnread) {
        setTotalUnread((count) => (count === null ? count : count + newUnread));
      }

      setSent((prev) => [
        ...created.map((item) => ({
          ...item,
          rest: recipientLabel,
        })),
        ...prev,
      ]);

      resetSendState();
      setActiveTab(TABS.INBOX);
      showToast(t.send.success);
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const setSelectedTemplate = (id) => {
    setSendState((prev) => ({ ...prev, selectedTemplateId: id }));
  };

  return (
    <div
      className={`lud-notifications-page ${ar ? "is-rtl" : "is-ltr"}`}
      dir={ar ? "rtl" : "ltr"}
    >
      {notice && (
        <div className="lud-notif-notice" role="alert">
          <NoticeBox variant="danger" title={notice.text}>
            {notice.detail && <p>{notice.detail}</p>}
          </NoticeBox>
          <Button
            variant="neutral"
            appearance="ghost"
            size="sm"
            icon={<X size={16} />}
            aria-label={ar ? "إغلاق التنبيه" : "Dismiss"}
            title={ar ? "إغلاق التنبيه" : "Dismiss"}
            onClick={() => setNotice(null)}
          />
        </div>
      )}

      {toast ? (
        <div className="lud-notif-floating-toast" role="status">
          {toast}
        </div>
      ) : null}

      {activeTab === TABS.INBOX ? (
        <NotificationsInbox
          restaurants={restaurants}
          notifications={notifications}
          stats={stats}
          filters={filters}
          setFilters={setFilters}
          t={t}
          loading={listState.status === "loading"}
          error={
            listState.status === "error" ? (
              <div className="lud-notif-state">
                <NoticeBox
                  variant="danger"
                  title={
                    ar
                      ? "تعذّر تحميل الإشعارات"
                      : "Notifications could not be loaded"
                  }
                >
                  {listState.error && <p>{listState.error}</p>}
                  <Button
                    variant="danger"
                    appearance="outline"
                    icon={<RefreshCcw size={15} />}
                    onClick={() => setReloadKey((key) => key + 1)}
                  >
                    {ar ? "إعادة المحاولة" : "Retry"}
                  </Button>
                </NoticeBox>
              </div>
            ) : null
          }
          pendingIds={pendingIds}
          onMarkRead={markRead}
          onOpen={openNotification}
        />
      ) : null}

      {activeTab === TABS.SEND ? (
        <SendNotification
          restaurants={restaurants}
          templates={templates}
          lang={lang}
          t={t}
          state={sendState}
          setState={setSendState}
          selectedTemplate={selectedTemplate}
          onSelectTemplate={setSelectedTemplate}
          onSend={sendNotification}
          sending={sending}
          sent={sent}
        />
      ) : null}

      {activeTab === TABS.BUILDER ? (
        <TemplateBuilder
          templates={templates}
          lang={lang}
          t={t}
          toastSaved={showToast}
          onTemplatesChange={loadTemplates}
        />
      ) : null}
    </div>
  );
}
